/**
 * TagList.tsx - Display list of tags
 * 
 * CHANGE HISTORY:
 * - 251102_0627 [pending] danielbeer: Initial creation
 */

import React from 'react';
import type { Tag } from '../types';

export interface TagListProps {
  tags: string[] | Tag[];
  onTagClick?: (tag: string) => void;
  onTagRemove?: (tag: string) => void;
  colorCoded?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  tagColors?: Record<string, string>;
}

export const TagList: React.FC<TagListProps> = ({
  tags,
  onTagClick,
  onTagRemove,
  colorCoded = true,
  size = 'medium',
  className = '',
  tagColors = {}
}) => {
  // Normalize tags to get name and color
  const normalizedTags = tags.map(tag => {
    if (typeof tag === 'string') {
      return {
        name: tag,
        displayName: tag,
        color: tagColors[tag] || '#94a3b8'
      };
    }
    return {
      name: tag.name,
      displayName: tag.displayName,
      color: tag.color
    };
  });

  if (normalizedTags.length === 0) {
    return null;
  }

  return (
    <div className={`taggr-list taggr-list-${size} ${className}`}>
      {normalizedTags.map((tag, index) => (
        <span
          key={index}
          className={`taggr-tag taggr-tag-${size} ${onTagClick ? 'clickable' : ''}`}
          style={colorCoded ? { backgroundColor: tag.color } : undefined}
          onClick={() => onTagClick?.(tag.name)}
          role={onTagClick ? 'button' : undefined}
          tabIndex={onTagClick ? 0 : undefined}
          onKeyDown={(e) => {
            if (onTagClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onTagClick(tag.name);
            }
          }}
        >
          <span className="taggr-tag-text">{tag.displayName}</span>
          {onTagRemove && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onTagRemove(tag.name);
              }}
              className="taggr-tag-remove"
              aria-label={`Remove ${tag.displayName}`}
            >
              ×
            </button>
          )}
        </span>
      ))}
    </div>
  );
};
